import React, { useState, useRef, useEffect } from 'react';
import { useStudioTheme, motion, type ThemeMode } from '../studioTheme';
import { StatusDot, healthToAnimation, healthColor } from '../studioPrimitives';
import type { Workspace } from '../../../../data/agentCommandCenter';

// ── Theme toggle ──────────────────────────────────────────────────────────────

function ThemeToggle() {
  const { theme, setTheme, ST } = useStudioTheme();
  const isDark = theme === 'dark';
  const next: ThemeMode = isDark ? 'light' : 'dark';

  return (
    <button
      onClick={() => setTheme(next)}
      title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      style={{
        position:        'relative',
        width:           44,
        height:          24,
        borderRadius:    12,
        backgroundColor: isDark ? ST.accent.purpleSubtle : ST.bg.overlay,
        border:          `1px solid ${isDark ? ST.border.accent : ST.border.default}`,
        cursor:          'pointer',
        flexShrink:      0,
        padding:         0,
        transition:      motion(ST.motion.default),
      }}
    >
      <div style={{
        position:        'absolute',
        top:             3,
        left:            isDark ? 3 : 21,
        width:           16,
        height:          16,
        borderRadius:    '50%',
        backgroundColor: isDark ? ST.accent.purple : ST.accent.gold,
        display:         'flex',
        alignItems:      'center',
        justifyContent:  'center',
        fontSize:        9,
        transition:      motion('left 0.22s cubic-bezier(0.4,0,0.2,1)'),
        boxShadow:       `0 1px 4px ${isDark ? ST.accent.purpleGlow : ST.accent.goldGlow}`,
      }}>
        {isDark ? '🌙' : '☀'}
      </div>
    </button>
  );
}

// ── Sidebar ───────────────────────────────────────────────────────────────────

type NavKey = string | '__overview__';

export function SidebarNav({
  workspaces,
  selectedId,
  onSelect,
}: {
  workspaces: Workspace[];
  selectedId: string | null;
  onSelect:   (id: string | null) => void;
}) {
  const { ST } = useStudioTheme();

  // Floating pill
  const [pillStyle, setPillStyle] = useState<{ top: number; height: number; opacity: number }>({
    top: 0, height: 36, opacity: 0,
  });
  const itemRefs   = useRef<Map<NavKey, HTMLDivElement>>(new Map());
  const navRef     = useRef<HTMLDivElement>(null);
  const activeKey: NavKey = selectedId ?? '__overview__';

  // Hover tracking per item
  const [hoveredId, setHoveredId] = useState<NavKey | null>(null);

  useEffect(() => {
    const el        = itemRefs.current.get(activeKey);
    const container = navRef.current;
    if (!el || !container) return;
    const elRect  = el.getBoundingClientRect();
    const navRect = container.getBoundingClientRect();
    setPillStyle({ top: elRect.top - navRect.top, height: elRect.height, opacity: 1 });
  }, [activeKey, workspaces.length]);

  // Active palette for workspace-colored pill
  const activePalette = selectedId ? (ST.workspace[selectedId] ?? null) : null;
  const pillColor     = activePalette?.icon ?? ST.accent.purple;
  const pillGlow      = activePalette
    ? `${activePalette.icon}28`
    : ST.accent.purpleGlow;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>

      {/* ── Brand ─────────────────────────────────────────────────────── */}
      <div style={{
        padding:         '20px 16px 18px',
        borderBottom:    `1px solid ${ST.border.subtle}`,
        flexShrink:      0,
        backgroundImage: `linear-gradient(145deg, ${ST.accent.purpleSubtle} 0%, transparent 55%)`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* Logo mark */}
          <div style={{
            width:           36,
            height:          36,
            borderRadius:    ST.radius.md,
            backgroundImage: ST.gradient.accent,
            display:         'flex',
            alignItems:      'center',
            justifyContent:  'center',
            fontSize:        17,
            boxShadow:       `${ST.shadow.glow}, inset 0 1px 0 rgba(255,255,255,0.20)`,
            flexShrink:      0,
          }}>
            ⚡
          </div>
          <div>
            {/* Gradient text brand name */}
            <div style={{
              fontSize:             16,
              fontWeight:           800,
              fontFamily:           ST.font.heading,
              lineHeight:           1.1,
              letterSpacing:        '-0.035em',
              background:           ST.gradient.accent,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor:  'transparent',
              backgroundClip:       'text',
            }}>
              Studio OS
            </div>
            <div style={{
              fontSize:      9,
              color:         ST.text.disabled,
              fontFamily:    ST.font.sans,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              marginTop:     2,
            }}>
              Engineering OS
            </div>
          </div>
        </div>
      </div>

      {/* ── Navigation ────────────────────────────────────────────────── */}
      <div
        ref={navRef}
        style={{ flex: 1, padding: '10px 8px', overflowY: 'auto', position: 'relative' }}
      >
        {/* Floating workspace-colored pill */}
        <div style={{
          position:        'absolute',
          left:            8,
          right:           8,
          top:             pillStyle.top,
          height:          pillStyle.height,
          borderRadius:    ST.radius.md,
          backgroundColor: `${pillColor}14`,
          border:          `1px solid ${pillColor}38`,
          boxShadow:       `0 0 18px ${pillGlow}`,
          opacity:         pillStyle.opacity,
          pointerEvents:   'none',
          transition:      motion(
            `top 0.22s cubic-bezier(0.4,0,0.2,1), height 0.15s ease, opacity 0.15s ease, background-color 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease`
          ),
        }} />

        {/* Overview */}
        <div
          ref={el => { if (el) itemRefs.current.set('__overview__', el); }}
          onClick={() => onSelect(null)}
          onMouseEnter={() => setHoveredId('__overview__')}
          onMouseLeave={() => setHoveredId(null)}
          style={{
            position:   'relative', display: 'flex', alignItems: 'center',
            gap:        10, padding: '8px 12px', borderRadius: ST.radius.md,
            cursor:     'pointer', userSelect: 'none', marginBottom: 2,
          }}
        >
          <div style={{
            width:      22, height: 22, borderRadius: ST.radius.xs,
            display:    'flex', alignItems: 'center', justifyContent: 'center',
            fontSize:   12, flexShrink: 0,
            color:      selectedId === null ? ST.text.accent : ST.text.muted,
            transition: motion(ST.motion.fast),
          }}>
            ◈
          </div>
          <span style={{
            flex:       1,
            fontSize:   12,
            fontWeight: selectedId === null ? 600 : 400,
            color:      selectedId === null ? ST.text.accent : ST.text.secondary,
            fontFamily: ST.font.sans,
            letterSpacing: '0.01em',
            transition: motion(ST.motion.fast),
          }}>
            Overview
          </span>
          {/* Arrow indicator */}
          <span style={{
            fontSize:   11,
            color:      selectedId === null ? ST.text.accent : ST.text.disabled,
            opacity:    hoveredId === '__overview__' || selectedId === null ? 1 : 0,
            transform:  hoveredId === '__overview__' || selectedId === null ? 'translateX(0)' : 'translateX(-4px)',
            transition: motion('opacity 0.15s ease, transform 0.15s ease'),
            flexShrink: 0,
          }}>
            ›
          </span>
        </div>

        {/* Section label */}
        <div style={{
          fontSize:      9,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color:         ST.text.disabled,
          fontFamily:    ST.font.sans,
          fontWeight:    700,
          padding:       '12px 12px 6px',
        }}>
          Workspaces
        </div>

        {/* Workspace items */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {workspaces.map(ws => {
            const palette  = ST.workspace[ws.id] ?? ST.workspaceFallback;
            const isActive = selectedId === ws.id;
            const isHov    = hoveredId === ws.id;
            const hc       = healthColor(ws.state.health, ST);

            return (
              <div
                key={ws.id}
                ref={el => { if (el) itemRefs.current.set(ws.id, el); }}
                onClick={() => onSelect(ws.id)}
                onMouseEnter={() => setHoveredId(ws.id)}
                onMouseLeave={() => setHoveredId(null)}
                style={{
                  position:        'relative',
                  display:         'flex',
                  alignItems:      'center',
                  gap:             10,
                  padding:         '7px 12px',
                  borderRadius:    ST.radius.md,
                  cursor:          'pointer',
                  userSelect:      'none',
                  backgroundImage: isHov && !isActive
                    ? `radial-gradient(ellipse 55% 80% at 0% 50%, ${palette.icon}0a, transparent 65%)`
                    : 'none',
                }}
              >
                {/* Workspace icon */}
                <div style={{
                  width:           22,
                  height:          22,
                  borderRadius:    ST.radius.xs,
                  backgroundColor: isActive ? palette.bg : `${palette.icon}12`,
                  border:          `1px solid ${palette.icon}${isActive ? '50' : isHov ? '35' : '1a'}`,
                  display:         'flex',
                  alignItems:      'center',
                  justifyContent:  'center',
                  fontSize:        11,
                  flexShrink:      0,
                  boxShadow:       isActive ? `0 0 20px ${palette.icon}55` : isHov ? `0 0 12px ${palette.icon}35` : 'none',
                  transition:      motion('background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease'),
                }}>
                  {ws.emoji}
                </div>

                <span style={{
                  flex:          1,
                  fontSize:      12,
                  fontWeight:    isActive ? 600 : 400,
                  color:         isActive ? palette.icon : ST.text.secondary,
                  fontFamily:    ST.font.sans,
                  letterSpacing: '0.01em',
                  overflow:      'hidden',
                  textOverflow:  'ellipsis',
                  whiteSpace:    'nowrap',
                  textShadow:    isActive ? `0 0 12px ${palette.icon}40` : 'none',
                  transition:    motion('color 0.2s ease, text-shadow 0.2s ease'),
                }}>
                  {ws.name.replace(' Workspace', '')}
                </span>

                <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
                  <StatusDot color={hc} size={5} animate={healthToAnimation(ws.state.health)} />

                  {/* Arrow indicator */}
                  <span style={{
                    fontSize:   11,
                    color:      palette.icon,
                    opacity:    isActive || isHov ? 1 : 0,
                    transform:  isActive || isHov ? 'translateX(0)' : 'translateX(-4px)',
                    transition: motion('opacity 0.15s ease, transform 0.15s ease'),
                  }}>
                    ›
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── User footer ───────────────────────────────────────────────── */}
      <div style={{
        padding:    '12px 12px 16px',
        borderTop:  `1px solid ${ST.border.subtle}`,
        flexShrink: 0,
      }}>
        {/* Leon card */}
        <div style={{
          display:         'flex',
          alignItems:      'center',
          gap:             10,
          padding:         '10px 12px',
          borderRadius:    ST.radius.lg,
          backgroundColor: ST.bg.overlay,
          backgroundImage: `linear-gradient(135deg, ${ST.accent.purpleSubtle} 0%, transparent 50%)`,
          border:          `1px solid ${ST.border.subtle}`,
          marginBottom:    10,
        }}>
          {/* Animated ring avatar */}
          <div style={{ position: 'relative', flexShrink: 0, width: 32, height: 32 }}>
            <div
              className="studio-avatar-ring"
              style={{
                position:   'absolute',
                inset:      -2,
                borderRadius: '50%',
                background: 'conic-gradient(from 0deg, #7c3aed, #06b6d4, #a855f7, transparent 70%, #7c3aed)',
              }}
            />
            <div style={{
              position:        'absolute',
              inset:           1,
              borderRadius:    '50%',
              backgroundImage: 'linear-gradient(135deg, #7c3aed 0%, #06b6d4 100%)',
              display:         'flex',
              alignItems:      'center',
              justifyContent:  'center',
              fontSize:        13,
              fontWeight:      700,
              color:           'rgba(255,255,255,0.92)',
              fontFamily:      ST.font.sans,
            }}>
              L
            </div>
            <div style={{
              position:        'absolute',
              bottom:          0,
              right:           0,
              width:           8,
              height:          8,
              borderRadius:    '50%',
              backgroundColor: ST.status.healthy,
              border:          `1.5px solid ${ST.bg.base}`,
              boxShadow:       `0 0 6px ${ST.status.healthy}90`,
            }} />
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: 12, fontWeight: 600, color: ST.text.primary,
              fontFamily: ST.font.sans, lineHeight: 1.2,
            }}>
              Leon
            </div>
            <div style={{ fontSize: 10, color: ST.text.disabled, fontFamily: ST.font.sans }}>
              Studio Owner
            </div>
          </div>
        </div>

        {/* Theme toggle */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 2px',
        }}>
          <span style={{ fontSize: 10, color: ST.text.disabled, fontFamily: ST.font.sans }}>
            Appearance
          </span>
          <ThemeToggle />
        </div>
      </div>
    </div>
  );
}
